import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Sparkles, Zap, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Trip, TripComponent } from "@shared/schema";
import { useQuery } from "@tanstack/react-query";

interface QuickAIPlannerProps {
  tripId: number;
  tripName: string;
  dateRange: [Date, Date];
}

export default function QuickAIPlanner({ tripId, tripName, dateRange }: QuickAIPlannerProps) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const { toast } = useToast();
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Get full trip details
  const { data: trip } = useQuery<Trip>({
    queryKey: [`/api/trips/${tripId}`],
    enabled: !!tripId,
  });
  
  // Get trip components to check if there are existing components
  const { data: components = [] } = useQuery<TripComponent[]>({
    queryKey: [`/api/trips/${tripId}/components`],
    enabled: !!tripId,
  });
  
  const hasExistingComponents = components.length > 0;

  // Function to directly generate and save an itinerary without user interaction
  const handleQuickGenerate = async () => {
    // If there are existing components, show confirmation first
    if (hasExistingComponents && !showConfirm) {
      setShowConfirm(true);
      return;
    }
    
    if (isGenerating) return;
    
    setIsGenerating(true);
    setShowConfirm(false);
    
    try {
      // Generate a prompt based on trip details
      const startDate = dateRange[0].toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
      const endDate = dateRange[1].toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
      const duration = Math.floor((dateRange[1].getTime() - dateRange[0].getTime()) / (1000 * 60 * 60 * 24)) + 1;
      
      let generatedPrompt = `Please create a detailed day-by-day itinerary for my trip`;
      
      if (tripName) {
        generatedPrompt += ` to ${tripName}`;
      }
      
      // Add countries information if available
      if (trip?.countries && (trip.countries as string[]).length > 0) {
        const countryList = (trip.countries as string[]).join(", ");
        generatedPrompt += ` visiting ${countryList}`;
      }
      
      generatedPrompt += ` from ${startDate} to ${endDate} (${duration} days).`;
      
      // Add client information if available
      if (trip?.clientName) {
        generatedPrompt += ` This trip is for client ${trip.clientName}.`;
      }
      
      if (trip?.clientInterests) {
        generatedPrompt += ` The client is interested in ${trip.clientInterests}.`;
      }
      
      // Add assumption about departing from the US
      generatedPrompt += ` Assume I'm departing from the United States, so please include appropriate international flights.`;
      
      generatedPrompt += ` Please include a good mix of activities, food, transportation and accommodation options appropriate for this destination. I'd like a balanced itinerary with popular attractions and some local experiences.`;
      
      // Add instruction about avoiding either-or options
      generatedPrompt += ` Important: Do not create components with multiple options or alternatives within a single item (like "Option 1: X OR Option 2: Y"). Each component should be a single, clear recommendation, not a choice between options.`;
      
      // Add instruction about organizing by day
      generatedPrompt += ` Please organize the itinerary clearly by day, with appropriate morning, afternoon, and evening activities for each day.`;
      
      // Step 1: Generate the itinerary components
      toast({
        title: "Generating Itinerary",
        description: "Creating a personalized itinerary for your trip...",
      });
      
      const data = await apiRequest("POST", `/api/trips/${tripId}/generate-itinerary`, {
        prompt: generatedPrompt,
        startDate: dateRange[0].toISOString(),
        endDate: dateRange[1].toISOString(),
      });
      
      const components = data.components;
      
      if (!components || components.length === 0) {
        throw new Error("No components were generated");
      }
      
      // Step 2: Save all components directly to the trip
      toast({
        title: "Saving Itinerary",
        description: "Adding components to your trip...",
      });
      
      let successCount = 0;
      let failedCount = 0;
      
      // Process components sequentially
      for (const component of components) {
        try {
          // Ensure component has correct structure before sending
          const sanitizedComponent = {
            tripId: component.tripId,
            type: component.type,
            title: component.title || "Untitled Activity",
            description: component.description || null,
            dayIndex: component.dayIndex || 0,
            startDate: component.startDate,
            endDate: component.endDate || null,
            location: component.location || null,
            startTime: component.startTime || null,
            endTime: component.endTime || null,
            details: component.details || {}
          };
          
          await apiRequest("POST", "/api/components", sanitizedComponent);
          successCount++;
        } catch (err) {
          failedCount++;
          console.error(`Error processing component: ${component.title}`, err);
        }
      }
      
      // Day destinations are now directly created by the AI on the server side
      
      // Step 4: Refresh the trip data
      queryClient.invalidateQueries({ queryKey: [`/api/trips/${tripId}`] });
      queryClient.invalidateQueries({ queryKey: [`/api/trips/${tripId}/components`] });
      queryClient.invalidateQueries({ queryKey: [`/api/trips/${tripId}/day-destinations`] });
      queryClient.refetchQueries({ queryKey: [`/api/trips/${tripId}/components`] });
      
      // Show success message
      toast({
        title: "Success",
        description: `Added ${successCount} items to your itinerary${
          failedCount > 0 ? ` (${failedCount} items failed)` : ""
        }`,
      });
    } catch (error) {
      console.error("Error in Quick AI Plan:", error);
      toast({
        title: "Error",
        description: "Failed to generate or save itinerary",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // Cancel confirmation
  const handleCancel = () => {
    setShowConfirm(false);
  };
  
  return (
    <div className="relative">
      <Button
        variant="outline"
        size="sm"
        className="flex items-center gap-1 mr-3"
        onClick={handleQuickGenerate}
        disabled={isGenerating}
        ref={buttonRef}
        data-quick-ai-planner
      >
        {isGenerating ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin text-amber-500" />
            <span>Generating...</span>
          </>
        ) : (
          <>
            <Zap className="h-4 w-4 text-amber-500" />
            <span>Quick AI Plan</span>
          </>
        )}
      </Button>
      
      {/* Confirmation popup */}
      {showConfirm && (
        <div className="absolute z-50 right-0 top-full mt-2 w-64 p-4 bg-white dark:bg-gray-800 rounded-md shadow-lg border">
          <p className="text-sm font-medium mb-3">This trip already has components. Generate a new itinerary?</p>
          <div className="flex justify-between">
            <Button size="sm" variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button 
              size="sm" 
              variant="destructive" 
              onClick={() => handleQuickGenerate()}
            >
              Replace All
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}